import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useDebounced } from "@/hooks/useDirectory";

// Recherche d'une personne existante (pour rattacher à une entreprise, un groupe…)
export function PersonPicker({ label = "Ajouter une personne", onPick, excludeIds = [], testId = "person-picker" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const q = useDebounced(text, 250);
  const { data, isFetching } = useQuery({
    queryKey: ["person-picker", q],
    queryFn: async () => (await api.get(`/persons?q=${encodeURIComponent(q)}&page_size=10`)).data.items,
    enabled: q.length > 1,
  });
  const items = (data || []).filter((p) => !excludeIds.includes(p.id));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="rounded-full bg-white" data-testid={testId}><UserPlus className="h-4 w-4" /> {label}</Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Nom, email…" value={text} onValueChange={setText} data-testid={`${testId}-search`} />
          <CommandList>
            {q.length < 2 && <div className="px-3 py-4 text-xs text-muted-foreground">Tapez au moins 2 lettres.</div>}
            {q.length > 1 && !isFetching && !items.length && <CommandEmpty>Aucune personne trouvée</CommandEmpty>}
            <CommandGroup>
              {items.map((p) => (
                <CommandItem key={p.id} value={p.id} onSelect={() => { onPick(p); setOpen(false); setText(""); }} data-testid={`${testId}-option-${p.id}`}>
                  <span className="truncate font-semibold">{p.full_name}</span>
                  <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{p.organizations.map((o) => o.name).join(", ")}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
